import { getActiveSubscription } from "Components/ManageSubscriptions/Apis";
import { getToken } from "Shared/baseURL";
import {
  fetchListById,
  fetchListKeywords,
  fetchLists,
  fetchMyLists,
  fetchUserLists,
  getMostViewedKeywords,
  getMostViewedLists,
  getTrendingKeywords,
  getTrendingLists,
} from "api/listService";
import axios from "axios";
import keycloak from "keycloak";
import { useInfiniteQuery, useQuery } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { fetchLoggedInUserKeywords } from "./userKeywords";
import {
  SET_ENTITLEMENTS,
  SET_ENTITLEMENT_LIST_COUNT,
} from "../Redux/ActionTypes";
import { getFrontendErrorMessage } from "Shared/apiInterceptors";

const resolveUserIdentifier = (profile, fallbackUser) => {
  const candidates = [
    profile?.id,
    profile?.userId,
    profile?.user_id,
    fallbackUser?.id,
    fallbackUser?.userId,
    fallbackUser?.user_id,
    keycloak?.tokenParsed?.user_id,
    keycloak?.tokenParsed?.userId,
  ];

  const normalizeNumericCandidate = (value) => {
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : null;
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed.length) return null;
      const numeric = Number(trimmed);
      return Number.isFinite(numeric) ? numeric : null;
    }
    return null;
  };

  for (const candidate of candidates) {
    const normalized = normalizeNumericCandidate(candidate);
    if (normalized != null) {
      return normalized;
    }
  }

  return null;
};

const normalizeUserIdentifier = (userId) => {
  if (typeof userId === "number") {
    return Number.isFinite(userId) ? userId : null;
  }

  if (typeof userId === "string") {
    const trimmed = userId.trim();
    if (!trimmed.length) return null;
    const lowered = trimmed.toLowerCase();
    if (lowered === "undefined" || lowered === "null") return null;
    return trimmed;
  }

  return null;
};

const normalizeNumericIdentifier = (userId) => {
  if (typeof userId === "number") {
    return Number.isFinite(userId) ? userId : null;
  }

  if (typeof userId === "string") {
    const trimmed = userId.trim();
    if (!trimmed.length) return null;
    const numeric = Number(trimmed);
    return Number.isFinite(numeric) ? numeric : null;
  }

  return null;
};

const NETWORK_QUERY_STALE_TIME = 60 * 1000;
const NETWORK_QUERY_CACHE_TIME = 10 * 60 * 1000;
const NETWORK_QUERY_OPTIONS = {
  cacheTime: NETWORK_QUERY_CACHE_TIME,
  refetchOnWindowFocus: false,
  retry: 1,
  staleTime: NETWORK_QUERY_STALE_TIME,
};

const fetchProfileListSummary = async (userId) => {
  const normalizedId = normalizeUserIdentifier(userId);
  if (normalizedId == null) {
    throw new Error("userId is required to fetch personal lists");
  }

  const pagePayload = await fetchUserLists({
    userId: normalizedId,
    page: 0,
    size: 5,
    sort: ["likesCount,DESC", "name,ASC"],
  });

  return {
    lists: Array.isArray(pagePayload?.list) ? pagePayload.list : [],
    pagination: pagePayload?.pagination,
  };
};

export const useMyListData = () => {
  const { profile, user } = useSelector((state) => state?.user || {});
  const resolvedUserId = normalizeUserIdentifier(
    resolveUserIdentifier(profile, user),
  );

  return useQuery(
    ["get-user-lists", resolvedUserId],
    () => fetchProfileListSummary(resolvedUserId),
    {
      enabled: resolvedUserId != null,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  );
};

const fetchMyListCount = async (userId) => {
  const normalizedId = normalizeUserIdentifier(userId);
  if (normalizedId == null) {
    throw new Error("userId is required to fetch the personal list count");
  }

  const pagePayload = await fetchMyLists({
    page: 0,
    size: 1,
  });
  const total = Number(pagePayload?.pagination?.total);

  if (Number.isFinite(total) && total >= 0) {
    return total;
  }

  return Array.isArray(pagePayload?.list) ? pagePayload.list.length : 0;
};

export const useMyListCount = ({ enabled = true } = {}) => {
  const { profile, user } = useSelector((state) => state?.user || {});
  const resolvedUserId = normalizeUserIdentifier(
    resolveUserIdentifier(profile, user),
  );

  return useQuery(
    ["get-my-lists", resolvedUserId, "count"],
    () => fetchMyListCount(resolvedUserId),
    {
      enabled: enabled && resolvedUserId != null,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000,
    },
  );
};

const DEFAULT_FREE_MAX_LISTS_TOTAL = 7;
const ABOUT_REACH_LIMIT_PERCENT = 80;

export const useListCreationEntitlementStatus = ({
  currentListCount: providedCurrentListCount = null,
  fetchListCount = true,
} = {}) => {
  const dispatch = useDispatch();
  const {
    profile,
    user,
    entitlements: cachedEntitlements,
    entitlementUsage,
  } = useSelector((state) => state?.user || {});
  const resolvedUserId = normalizeUserIdentifier(
    resolveUserIdentifier(profile, user),
  );

  const hasProvidedListCount =
    providedCurrentListCount !== null &&
    providedCurrentListCount !== undefined &&
    Number.isFinite(Number(providedCurrentListCount)) &&
    Number(providedCurrentListCount) >= 0;
  const { data: queriedListCount, isLoading: isListCountLoading } =
    useMyListCount({
      enabled: fetchListCount,
    });
  const { data: queriedEntitlements, isLoading: isEntitlementsLoading } =
    useUserEntitlements(resolvedUserId, {
      enabled: resolvedUserId != null,
    });
  const entitlements = queriedEntitlements || cachedEntitlements;

  const currentCountCandidates = [
    hasProvidedListCount ? providedCurrentListCount : null,
    entitlementUsage?.currentListCount,
    queriedListCount,
    0,
  ]
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value >= 0);
  const currentCount =
    currentCountCandidates.length > 0 ? Math.max(...currentCountCandidates) : 0;

  const packageCode = String(entitlements?.packageCode || "FREE").toUpperCase();
  const configuredMaxLists = Number(entitlements?.limits?.maxListsTotal);
  const maxListsTotal =
    Number.isFinite(configuredMaxLists) && configuredMaxLists > 0
      ? configuredMaxLists
      : packageCode === "FREE"
        ? DEFAULT_FREE_MAX_LISTS_TOTAL
        : 0;

  const usagePercent =
    maxListsTotal > 0 ? Math.min((currentCount / maxListsTotal) * 100, 100) : 0;

  useEffect(() => {
    if (queriedEntitlements) {
      dispatch({
        type: SET_ENTITLEMENTS,
        payload: queriedEntitlements,
      });
    }
  }, [dispatch, queriedEntitlements]);

  useEffect(() => {
    const fetchedCount = Number(
      hasProvidedListCount ? providedCurrentListCount : queriedListCount,
    );

    if (Number.isFinite(fetchedCount) && fetchedCount >= 0) {
      dispatch({
        type: SET_ENTITLEMENT_LIST_COUNT,
        payload: fetchedCount,
      });
    }
  }, [
    dispatch,
    hasProvidedListCount,
    providedCurrentListCount,
    queriedListCount,
  ]);

  return {
    packageCode,
    source: entitlements?.source || null,
    currentCount,
    maxListsTotal,
    remainingCount:
      maxListsTotal > 0 ? Math.max(maxListsTotal - currentCount, 0) : 0,
    usagePercent,
    isLoading:
      isEntitlementsLoading ||
      (fetchListCount && isListCountLoading),
    limitReached: maxListsTotal > 0 && currentCount >= maxListsTotal,
    aboutToReach:
      maxListsTotal > 0 &&
      currentCount < maxListsTotal &&
      usagePercent >= ABOUT_REACH_LIMIT_PERCENT,
  };
};

export const useListKeywordEntitlementStatus = (
  listId,
  { currentKeywordCount = null, enabled = true } = {},
) => {
  const { profile, user } = useSelector((state) => state?.user || {});
  const resolvedUserId = normalizeUserIdentifier(
    resolveUserIdentifier(profile, user),
  );

  const { data: entitlements, isLoading: isEntitlementsLoading } =
    useUserEntitlements(resolvedUserId, {
      enabled: enabled && resolvedUserId != null,
    });

  const normalizedListId = normalizeUserIdentifier(listId);
  const hasExplicitCurrentKeywordCount =
    currentKeywordCount !== null &&
    currentKeywordCount !== undefined &&
    currentKeywordCount !== "" &&
    Number.isFinite(Number(currentKeywordCount));
  const shouldFetchList =
    enabled && normalizedListId != null && !hasExplicitCurrentKeywordCount;

  const { data: listDetails, isLoading: isListLoading } = useQuery(
    ["list-keyword-entitlement-status", normalizedListId],
    () => fetchListById(normalizedListId),
    {
      enabled: shouldFetchList,
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  );

  const countCandidates = [
    hasExplicitCurrentKeywordCount ? currentKeywordCount : null,
    listDetails?.keywordsCount,
    listDetails?.keywordCount,
    listDetails?.totalKeywords,
    Array.isArray(listDetails?.keywords) ? listDetails.keywords.length : null,
  ]
    .filter((value) => value !== null && value !== undefined && value !== "")
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value >= 0);

  const resolvedCurrentKeywordCount =
    countCandidates.length > 0 ? Math.max(...countCandidates) : 0;

  const configuredMaxKeywords = Number(
    entitlements?.limits?.maxKeywordsPerList,
  );
  const packageCode = String(entitlements?.packageCode || "FREE").toUpperCase();
  const maxKeywordsPerList =
    Number.isFinite(configuredMaxKeywords) && configuredMaxKeywords > 0
      ? configuredMaxKeywords
      : 0;

  const usagePercent =
    maxKeywordsPerList > 0
      ? Math.min((resolvedCurrentKeywordCount / maxKeywordsPerList) * 100, 100)
      : 0;
  const remainingKeywordCapacity =
    maxKeywordsPerList > 0
      ? Math.max(maxKeywordsPerList - resolvedCurrentKeywordCount, 0)
      : 0;
  const limitReached =
    maxKeywordsPerList > 0 && resolvedCurrentKeywordCount >= maxKeywordsPerList;

  return {
    packageCode,
    currentKeywordCount: resolvedCurrentKeywordCount,
    maxKeywordsPerList,
    remainingKeywordCapacity,
    usagePercent,
    isLoading: isEntitlementsLoading || isListLoading,
    limitReached,
    aboutToReach:
      maxKeywordsPerList > 0 &&
      resolvedCurrentKeywordCount < maxKeywordsPerList &&
      usagePercent >= ABOUT_REACH_LIMIT_PERCENT,
  };
};

export const useListSharingEntitlementStatus = () => {
  const { profile, user } = useSelector((state) => state?.user || {});
  const resolvedUserId = normalizeUserIdentifier(
    resolveUserIdentifier(profile, user),
  );

  const { data: entitlements, isLoading } = useUserEntitlements(
    resolvedUserId,
    {
      enabled: resolvedUserId != null,
    },
  );
  const packageCode = String(entitlements?.packageCode || "FREE").toUpperCase();
  const canShareLists = ["ENHANCED", "PREMIUM"].includes(packageCode);

  return {
    packageCode,
    canShareLists,
    restrictionMessage:
      "List sharing is available on Enhanced and Premium plans.",
    isLoading,
  };
};

export const useListCopyingEntitlementStatus = () => {
  const { profile, user } = useSelector((state) => state?.user || {});
  const resolvedUserId = normalizeUserIdentifier(
    resolveUserIdentifier(profile, user),
  );

  const { data: entitlements, isLoading } = useUserEntitlements(
    resolvedUserId,
    {
      enabled: resolvedUserId != null,
    },
  );
  const packageCode = String(entitlements?.packageCode || "FREE").toUpperCase();
  const canCopyLists = ["ENHANCED", "PREMIUM"].includes(packageCode);

  return {
    packageCode,
    canCopyLists,
    restrictionMessage:
      "List copying is available only on Enhanced and Premium plans.",
    isLoading,
  };
};

export const useAiCreationEntitlementStatus = () => {
  const { profile, user } = useSelector((state) => state?.user || {});
  const resolvedUserId = normalizeUserIdentifier(
    resolveUserIdentifier(profile, user),
  );

  const { data: entitlements, isLoading } = useUserEntitlements(
    resolvedUserId,
    {
      enabled: resolvedUserId != null,
    },
  );
  const packageCode = String(entitlements?.packageCode || "FREE").toUpperCase();
  const kailPromptLimit = Number(
    entitlements?.limits?.kailUsageCapByPrompts ?? 0,
  );
  const bulkGenerationLimit = Number(
    entitlements?.limits?.aiKeywordBulkGenerationCap ??
      entitlements?.limits?.monthlyAiListCreations ??
      entitlements?.limits?.monthlyAiKeywordCreations ??
      0,
  );
  const keywordDefinitionRegenerationsPerKeyword = Number(
    entitlements?.limits?.keywordDefinitionRegenerationsPerKeyword ??
      entitlements?.limits?.monthlyAiDefinitionGenerations ??
      0,
  );
  const kailPromptsUsed = Number(
    entitlements?.usage?.kailUsageCapByPrompts ?? 0,
  );
  const bulkGenerationsUsed = Number(
    entitlements?.usage?.aiKeywordBulkGenerationCap ??
      entitlements?.usage?.monthlyAiListCreations ??
      entitlements?.usage?.monthlyAiKeywordCreations ??
      0,
  );
  const keywordDefinitionRegenerationsUsed = Number(
    entitlements?.usage?.keywordDefinitionRegenerationsPerKeyword ??
      entitlements?.usage?.monthlyAiDefinitionGenerations ??
      0,
  );
  const normalizedKailPromptLimit =
    Number.isFinite(kailPromptLimit) && kailPromptLimit > 0
      ? kailPromptLimit
      : 0;
  const normalizedBulkGenerationLimit =
    Number.isFinite(bulkGenerationLimit) && bulkGenerationLimit > 0
      ? bulkGenerationLimit
      : 0;
  const normalizedKeywordDefinitionRegenerationLimit =
    Number.isFinite(keywordDefinitionRegenerationsPerKeyword) &&
    keywordDefinitionRegenerationsPerKeyword > 0
      ? keywordDefinitionRegenerationsPerKeyword
      : 0;
  const normalizedKailPromptsUsed =
    Number.isFinite(kailPromptsUsed) && kailPromptsUsed > 0
      ? Math.min(kailPromptsUsed, normalizedKailPromptLimit)
      : 0;
  const normalizedBulkGenerationsUsed =
    Number.isFinite(bulkGenerationsUsed) && bulkGenerationsUsed > 0
      ? Math.min(bulkGenerationsUsed, normalizedBulkGenerationLimit)
      : 0;
  const normalizedKeywordDefinitionRegenerationsUsed =
    Number.isFinite(keywordDefinitionRegenerationsUsed) &&
    keywordDefinitionRegenerationsUsed > 0
      ? Math.min(
          keywordDefinitionRegenerationsUsed,
          normalizedKeywordDefinitionRegenerationLimit,
        )
      : 0;
  const remainingKailPrompts =
    normalizedKailPromptLimit > 0
      ? Math.max(normalizedKailPromptLimit - normalizedKailPromptsUsed, 0)
      : 0;
  const remainingBulkGenerations =
    normalizedBulkGenerationLimit > 0
      ? Math.max(
          normalizedBulkGenerationLimit - normalizedBulkGenerationsUsed,
          0,
        )
      : 0;
  const remainingKeywordDefinitionRegenerations =
    normalizedKeywordDefinitionRegenerationLimit > 0
      ? Math.max(
          normalizedKeywordDefinitionRegenerationLimit -
            normalizedKeywordDefinitionRegenerationsUsed,
          0,
        )
      : 0;
  const hasAiDefinitionRegenerationFeature =
    ["ENHANCED", "PREMIUM"].includes(packageCode) ||
    Boolean(entitlements?.features?.aiDefinitionRegeneration);
  const canUseKailPrompts =
    normalizedKailPromptLimit > 0 && remainingKailPrompts > 0;
  const canUseBulkKeywordGeneration =
    normalizedBulkGenerationLimit > 0 && remainingBulkGenerations > 0;
  const canUseKeywordDefinitionRegeneration =
    normalizedKeywordDefinitionRegenerationLimit > 0;

  return {
    packageCode,
    source: entitlements?.source || null,
    period: entitlements?.period || { start: null, end: null },
    kailPromptLimit: normalizedKailPromptLimit,
    kailPromptsUsed: normalizedKailPromptsUsed,
    remainingKailPrompts,
    bulkGenerationLimit: normalizedBulkGenerationLimit,
    bulkGenerationsUsed: normalizedBulkGenerationsUsed,
    remainingBulkGenerations,
    keywordDefinitionRegenerationsPerKeyword:
      normalizedKeywordDefinitionRegenerationLimit,
    keywordDefinitionRegenerationsUsed:
      normalizedKeywordDefinitionRegenerationsUsed,
    remainingKeywordDefinitionRegenerations,
    monthlyAiListCreations: normalizedBulkGenerationLimit,
    monthlyAiKeywordCreations: normalizedBulkGenerationLimit,
    monthlyAiDefinitionGenerations:
      normalizedKeywordDefinitionRegenerationLimit,
    aiListCreationsUsed: normalizedBulkGenerationsUsed,
    aiKeywordCreationsUsed: normalizedBulkGenerationsUsed,
    aiDefinitionGenerationsUsed: normalizedKeywordDefinitionRegenerationsUsed,
    remainingAiListCreations: remainingBulkGenerations,
    remainingAiKeywordCreations: remainingBulkGenerations,
    remainingAiDefinitionGenerations: remainingKeywordDefinitionRegenerations,
    canUseKailPrompts,
    canUseBulkKeywordGeneration,
    canUseKeywordDefinitionRegeneration,
    canUseAiListCreation: canUseBulkKeywordGeneration,
    canUseAiKeywordCreation: canUseBulkKeywordGeneration,
    canUseAiDefinitionGeneration: canUseKeywordDefinitionRegeneration,
    hasAiDefinitionRegenerationEntitlement:
      hasAiDefinitionRegenerationFeature ||
      normalizedKeywordDefinitionRegenerationLimit > 0,
    aiListCreationTrackingStrategy:
      entitlements?.strategies?.aiKeywordBulkGenerationCap ||
      entitlements?.strategies?.monthlyAiListCreations ||
      entitlements?.strategies?.monthlyAiKeywordCreations ||
      null,
    aiKeywordCreationTrackingStrategy:
      entitlements?.strategies?.aiKeywordBulkGenerationCap ||
      entitlements?.strategies?.monthlyAiKeywordCreations ||
      null,
    aiDefinitionGenerationTrackingStrategy:
      entitlements?.strategies?.keywordDefinitionRegenerationsPerKeyword ||
      entitlements?.strategies?.monthlyAiDefinitionGenerations ||
      null,
    isLoading,
  };
};

export const useGameplayEntitlementStatus = () => {
  const { profile, user } = useSelector((state) => state?.user || {});
  const resolvedUserId = normalizeUserIdentifier(
    resolveUserIdentifier(profile, user),
  );

  const { data: entitlements, isLoading } = useUserEntitlements(
    resolvedUserId,
    {
      enabled: resolvedUserId != null,
    },
  );

  const normalizePositiveLimit = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue > 0
      ? numericValue
      : 0;
  };
  const monthlyOwnGamesReplays = normalizePositiveLimit(
    entitlements?.limits?.monthlyOwnGamesReplays,
  );
  const monthlyChallengedGamesPlay = normalizePositiveLimit(
    entitlements?.limits?.monthlyChallengedGamesPlay,
  );
  const monthlyChallengedListsPlay = normalizePositiveLimit(
    entitlements?.limits?.monthlyChallengedListsPlay,
  );
  const ownGamesReplaysUsed = normalizePositiveLimit(
    entitlements?.usage?.monthlyOwnGamesReplays,
  );
  const challengedGamesPlayUsed = normalizePositiveLimit(
    entitlements?.usage?.monthlyChallengedGamesPlay,
  );
  const challengedListsPlayUsed = normalizePositiveLimit(
    entitlements?.usage?.monthlyChallengedListsPlay,
  );
  const canUseCoreQuizzes = Boolean(entitlements?.features?.coreQuizzes);
  const learningInsights = String(
    entitlements?.features?.learningInsights || "BASIC",
  ).toUpperCase();
  const hasEarlyAccess = Boolean(entitlements?.features?.earlyAccess);
  const canPlayGames = canUseCoreQuizzes;

  let accessMessage = "";
  if (!canUseCoreQuizzes) {
    accessMessage =
      "Games are not included in your current plan. Upgrade to access games.";
  }

  return {
    packageCode: entitlements?.packageCode || "FREE",
    source: entitlements?.source || null,
    period: entitlements?.period || { start: null, end: null },
    monthlyOwnGamesReplays,
    monthlyChallengedGamesPlay,
    monthlyChallengedListsPlay,
    ownGamesReplaysUsed,
    challengedGamesPlayUsed,
    challengedListsPlayUsed,
    remainingOwnGamesReplays:
      monthlyOwnGamesReplays > 0
        ? Math.max(monthlyOwnGamesReplays - ownGamesReplaysUsed, 0)
        : 0,
    remainingChallengedGamesPlay:
      monthlyChallengedGamesPlay > 0
        ? Math.max(monthlyChallengedGamesPlay - challengedGamesPlayUsed, 0)
        : 0,
    remainingChallengedListsPlay:
      monthlyChallengedListsPlay > 0
        ? Math.max(monthlyChallengedListsPlay - challengedListsPlayUsed, 0)
        : 0,
    monthlyGamesPlayed: 0,
    gamesPlayedUsed: 0,
    remainingGamesPlayed: 0,
    canUseCoreQuizzes,
    canPlayGames,
    hasEarlyAccess,
    learningInsights,
    hasAdvancedInsights: learningInsights === "ADVANCED",
    accessMessage,
    gameplayTrackingStrategy: null,
    ownGameReplayTrackingStrategy:
      entitlements?.strategies?.monthlyOwnGamesReplays || null,
    challengedGamesPlayTrackingStrategy:
      entitlements?.strategies?.monthlyChallengedGamesPlay || null,
    challengedListsPlayTrackingStrategy:
      entitlements?.strategies?.monthlyChallengedListsPlay || null,
    isLoading,
  };
};

export const useUserListData = (userId) => {
  const normalizedId = normalizeUserIdentifier(userId);
  return useQuery(
    ["get-user-lists", normalizedId],
    () => fetchProfileListSummary(normalizedId),
    {
      enabled: normalizedId != null,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  );
};

const normalizeKeywordCollection = (payload) => {
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.keywords)) return payload.keywords;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.list)) return payload.list;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const fetchMyKeywords = async (userId) => {
  const normalizedId = normalizeUserIdentifier(userId);
  if (normalizedId == null) {
    throw new Error("userId is required to fetch personal keywords");
  }

  const token = await getToken();
  const authHeaders = { Authorization: token };
  const listsRes = await axios.get("/ks/api/lists", {
    headers: authHeaders,
    params: {
      page: 0,
      size: 20,
      sort: "createdAt,DESC",
      createdBy: normalizedId,
      q: "",
    },
  });

  const rawLists = normalizeKeywordCollection(listsRes?.data);
  const listHrefs = rawLists
    .map((list) => list?.href ?? list?.uuid ?? list?.id)
    .filter(Boolean)
    .slice(0, 10);

  if (!listHrefs.length) {
    return [];
  }

  const keywordResponses = await Promise.all(
    listHrefs.map((href) =>
      axios
        .get(`/ks/api/lists/${href}/keywords`, {
          headers: authHeaders,
          params: {
            pg: JSON.stringify({
              page: 0,
              size: 30,
            }),
          },
        })
        .catch(() => null),
    ),
  );

  const seenKeywords = new Set();
  const keywordItems = [];

  keywordResponses.forEach((res) => {
    const rawKeywords = normalizeKeywordCollection(res?.data);

    rawKeywords.forEach((item) => {
      const term = String(
        item?.term ?? item?.name ?? item?.keyword ?? item?.title ?? "",
      ).trim();
      if (!term) return;

      const keywordId = item?.uuid ?? item?.id ?? item?.keywordId ?? null;
      const dedupeKey = keywordId ? String(keywordId) : term.toLowerCase();
      if (seenKeywords.has(dedupeKey)) return;

      seenKeywords.add(dedupeKey);
      keywordItems.push({
        ...item,
        id: item?.id ?? item?.keywordId ?? keywordId,
        uuid: keywordId,
        term,
      });
    });
  });

  return keywordItems;
};

export const useMyKeywords = () => {
  const { profile, user } = useSelector((state) => state?.user || {});
  const resolvedUserId = normalizeUserIdentifier(
    resolveUserIdentifier(profile, user),
  );
  const resolvedUser = profile || user || null;

  return useQuery(
    ["get-my-keywords", resolvedUserId],
    () =>
      fetchLoggedInUserKeywords({
        user: resolvedUser,
      }),
    {
      enabled: resolvedUserId != null && resolvedUser != null,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: true,
      staleTime: 60 * 1000,
    },
  );
};

const GLOBAL_SEARCH_LISTS_SIZE = 40;
const GLOBAL_SEARCH_KEYWORD_LIST_LIMIT = 20;
const GLOBAL_SEARCH_KEYWORDS_PER_LIST = 25;

const fetchGlobalSearchCatalog = async ({
  size = GLOBAL_SEARCH_LISTS_SIZE,
  keywordListLimit = GLOBAL_SEARCH_KEYWORD_LIST_LIMIT,
  keywordsPerList = GLOBAL_SEARCH_KEYWORDS_PER_LIST,
} = {}) => {
  const listsPage = await fetchLists({
    page: 0,
    size,
    sort: "createdAt,DESC",
  });

  const lists = Array.isArray(listsPage?.lists)
    ? listsPage.lists
    : Array.isArray(listsPage?.list)
      ? listsPage.list
      : [];

  const candidateLists = lists.slice(0, keywordListLimit);
  const keywordResponses = await Promise.all(
    candidateLists.map(async (listItem) => {
      const sourceListId =
        listItem?.id ?? listItem?.uuid ?? listItem?.href ?? null;
      const sourceListName =
        listItem?.name || listItem?.title || listItem?.listName || "";
      const sourceSubject = listItem?.subject || listItem?.subjectName || null;

      const embeddedKeywords = Array.isArray(listItem?.keywords)
        ? listItem.keywords
        : [];

      if (embeddedKeywords.length > 0) {
        return embeddedKeywords.map((keyword) => ({
          ...keyword,
          listId: sourceListId,
          listName: sourceListName,
          subject: keyword?.subject || sourceSubject,
          subjectName:
            keyword?.subjectName ||
            (typeof sourceSubject === "string"
              ? sourceSubject
              : sourceSubject?.name || sourceSubject?.title || ""),
        }));
      }

      if (sourceListId == null || sourceListId === "") {
        return [];
      }

      try {
        const keywordsPage = await fetchListKeywords(sourceListId, {
          page: 0,
          size: keywordsPerList,
        });

        const pageKeywords = Array.isArray(keywordsPage?.list)
          ? keywordsPage.list
          : [];

        return pageKeywords.map((keyword) => ({
          ...keyword,
          listId: sourceListId,
          listName: sourceListName,
          subject: keyword?.subject || sourceSubject,
          subjectName:
            keyword?.subjectName ||
            (typeof sourceSubject === "string"
              ? sourceSubject
              : sourceSubject?.name || sourceSubject?.title || ""),
        }));
      } catch {
        return [];
      }
    }),
  );

  const dedupedKeywords = [];
  const seenKeywordKeys = new Set();

  keywordResponses.flat().forEach((keyword) => {
    const keywordTerm = String(
      keyword?.term ??
        keyword?.name ??
        keyword?.keyword ??
        keyword?.title ??
        "",
    ).trim();
    const dedupeKey = keyword?.uuid || keyword?.id || keywordTerm.toLowerCase();

    if (!dedupeKey || seenKeywordKeys.has(dedupeKey)) {
      return;
    }

    seenKeywordKeys.add(dedupeKey);
    dedupedKeywords.push({
      ...keyword,
      term:
        keywordTerm || keyword?.term || keyword?.name || keyword?.keyword || "",
    });
  });

  return {
    lists,
    keywords: dedupedKeywords,
    pagination: listsPage?.pagination || {
      page: 1,
      pageIndex: 0,
      size,
      total: lists.length,
      totalPages: lists.length > 0 ? 1 : 0,
    },
  };
};

export const useGlobalSearchCatalog = (options = {}) => {
  const {
    enabled = true,
    size = GLOBAL_SEARCH_LISTS_SIZE,
    keywordListLimit = GLOBAL_SEARCH_KEYWORD_LIST_LIMIT,
    keywordsPerList = GLOBAL_SEARCH_KEYWORDS_PER_LIST,
  } = options;

  return useQuery(
    ["global-search-catalog", size, keywordListLimit, keywordsPerList],
    () =>
      fetchGlobalSearchCatalog({
        size,
        keywordListLimit,
        keywordsPerList,
      }),
    {
      enabled,
      cacheTime: 10 * 60 * 1000,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
};

const getSpecificUserProfile = async (id) => {
  const token = await getToken();
  return axios
    .get(`/sns/v1/users/${id}`, {
      headers: {
        Authorization: token,
      },
    })
    .then((res) => res.data)
    .catch((error) => {
      if (error.response) {
        throw new Error(getFrontendErrorMessage(error));
      }
      throw new Error(getFrontendErrorMessage(error));
    });
};

/**
 * @param {string} id id of user
 * @returns data of user
 */
export const useSpecificUserData = (id, options = {}) => {
  const { enabled = true } = options;
  const normalizedId = normalizeUserIdentifier(id);
  return useQuery(
    ["get-specific-user-data", normalizedId],
    () => getSpecificUserProfile(normalizedId),
    {
      enabled: enabled && normalizedId != null,
      ...NETWORK_QUERY_OPTIONS,
    },
  );
};

const getSpecificUserProfileByUsername = async (username) => {
  const token = await getToken();
  return axios
    .get(`/sns/v1/users/${username}`, {
      headers: {
        Authorization: token,
      },
    })
    .then((res) => res.data)
    .catch((error) => {
      if (error.response) {
        const { status } = error.response;
        let message = getFrontendErrorMessage(error);
        if (status === 404) {
          message = "Oops, it looks like that user profile does not exist";
        }
        throw new Error(message);
      }
      throw new Error(getFrontendErrorMessage(error));
    });
};

export const useSpecificUserDataByUsername = (username) => {
  return useQuery(
    ["get-specific-user-data-by-username", username],
    () => getSpecificUserProfileByUsername(username),
    {
      enabled: !!username,
      ...NETWORK_QUERY_OPTIONS,
    },
  );
};

const buildSnsParams = ({
  filters: inputFilters,
  userId,
  filterType,
  status,
  page = 0,
  size = 20,
  sort = "createdAt,DESC",
} = {}) => {
  const filters = {
    ...(inputFilters && typeof inputFilters === "object" ? inputFilters : {}),
  };
  if (userId != null && userId !== "") {
    filters.userId = String(userId);
  }
  if (filterType) {
    filters.filterType = String(filterType);
  }
  if (status) {
    filters.status = String(status);
  }

  const sortValues = Array.isArray(sort) ? sort : [sort];

  return {
    filters: JSON.stringify(filters),
    pageable: JSON.stringify({ page, size, sort: sortValues }),
    sort: JSON.stringify({ sort: sortValues }),
  };
};

const normalizeSuggestionsParams = (input) => {
  if (input && typeof input === "object" && !Array.isArray(input)) {
    return input;
  }

  return {
    publicId: input,
  };
};

const getSuggestions = async (options = {}) => {
  const {
    page = 0,
    size = 30,
    sort = [],
  } = normalizeSuggestionsParams(options);
  const sortValues = Array.isArray(sort) ? sort : sort ? [sort] : [];
  const token = await getToken();
  return axios
    .get("/sns/v1/suggestions", {
      headers: { Authorization: token },
      params: {
        pageable: JSON.stringify({
          page: Number(page) || 0,
          size: Number(size) || 30,
          sort: sortValues,
        }),
      },
    })
    .then((res) => res.data);
};

export const useSuggestions = (options) => {
  const {
    enabled = true,
    page = 0,
    publicId,
    userId,
    size = 30,
    sort = [],
  } = normalizeSuggestionsParams(options);
  const normalizedRequesterId = normalizeUserIdentifier(publicId ?? userId);
  const sortValues = Array.isArray(sort) ? sort : sort ? [sort] : [];

  return useQuery(
    ["get-suggestions", normalizedRequesterId ?? null, page, size, sortValues],
    () => getSuggestions({ page, size, sort: sortValues }),
    {
      enabled: enabled && normalizedRequesterId != null,
      ...NETWORK_QUERY_OPTIONS,
    },
  );
};

const normalizeDiscoverUsersParams = (input) => {
  if (input && typeof input === "object" && !Array.isArray(input)) {
    return input;
  }

  return {
    query: input,
  };
};

const getDiscoverUsers = async (options = {}) => {
  const {
    page = 0,
    query = "",
    size = 30,
    sort = [],
  } = normalizeDiscoverUsersParams(options);
  const normalizedQuery = String(query || "").trim();
  const sortValues = Array.isArray(sort) ? sort : sort ? [sort] : [];
  const token = await getToken();

  return axios
    .get("/sns/v1/users/discover", {
      headers: { Authorization: token },
      params: {
        query: normalizedQuery,
        page: Number(page) || 0,
        size: Number(size) || 30,
        sort: sortValues,
      },
    })
    .then((res) => res.data);
};

export const useDiscoverUsers = (options) => {
  const {
    enabled = true,
    page = 0,
    query = "",
    size = 30,
    sort = [],
  } = normalizeDiscoverUsersParams(options);
  const normalizedQuery = String(query || "").trim();
  const sortValues = Array.isArray(sort) ? sort : sort ? [sort] : [];

  return useQuery(
    ["discover-users", normalizedQuery, page, size, sortValues],
    () =>
      getDiscoverUsers({
        page,
        query: normalizedQuery,
        size,
        sort: sortValues,
      }),
    {
      enabled: enabled && normalizedQuery.length > 0,
      ...NETWORK_QUERY_OPTIONS,
    },
  );
};

const getSentChallenges = async (page = 1, sort = "createdAt.DESC") => {
  const token = await getToken();
  return axios
    .get("/users/me/sent-challenges.json", {
      headers: {
        Authorization: token,
      },
      params: {
        size: 10,
        page,
        sort,
      },
    })
    .then((res) => res.data);
};

export const useSentChallenges = () => {
  return useInfiniteQuery({
    queryKey: ["get-sent-challenges"],
    queryFn: ({ pageParam = 1 }) => getSentChallenges(pageParam),
    getNextPageParam: (lastPage) => {
      const { pagination } = lastPage;
      if (pagination.page < pagination.totalPages) {
        return pagination.page + 1;
      } else {
        return undefined;
      }
    },
  });
};

const getReceivedChallenges = async (page = 1, sort = "createdAt.DESC") => {
  const token = await getToken();
  return axios
    .get("/users/me/received-challenges.json", {
      headers: {
        Authorization: token,
      },
      params: {
        page,
        size: 10,
        sort,
      },
    })
    .then((res) => res.data);
};

export const useReceivedChallenges = () => {
  return useInfiniteQuery({
    queryKey: ["get-received-challenges"],
    queryFn: ({ pageParam = 1 }) => getReceivedChallenges(pageParam),
    getNextPageParam: (lastPage) => {
      const { pagination } = lastPage;
      if (pagination.page < pagination.totalPages) {
        return pagination.page + 1;
      } else {
        return undefined;
      }
    },
  });
};

// New connections API
const logSnsError = (label, error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  console.error(`[SNS] ${label} failed`, { status, data });
  throw error;
};

const getConnections = async (userId) => {
  const token = await getToken();
  const params = {
    userId: userId != null ? String(userId) : "",
  };
  return axios
    .get(`/sns/v1/connections`, {
      headers: { Authorization: token },
      params,
    })
    .then((res) => res.data)
    .catch((error) => logSnsError("connections", error));
};

export const useConnections = (userId, options = {}) => {
  const { enabled = true } = options;

  return useQuery(["get-connections", userId], () => getConnections(userId), {
    enabled: Boolean(userId) && enabled,
    ...NETWORK_QUERY_OPTIONS,
  });
};

// Connection requests API
const getConnectionRequests = async (userId, filterType, status) => {
  const token = await getToken();
  const params = {
    ...(filterType ? { filterType } : {}),
    ...(userId != null ? { userId: String(userId) } : {}),
    ...(status ? { status } : {}),
  };
  return axios
    .get(`/sns/v1/connections/requests`, {
      headers: { Authorization: token },
      params,
    })
    .then((res) => res.data)
    .catch((error) => logSnsError("connection-requests", error));
};

// eslint-disable-next-line no-unused-vars
const createConnectionRequest = async (toUserId, message) => {
  const token = await getToken();
  return axios
    .post(
      `/sns/v1/connections/requests`,
      { toUserId, message },
      {
        headers: { Authorization: token },
      },
    )
    .then((res) => res.data);
};

// eslint-disable-next-line no-unused-vars
const updateConnectionRequest = async (id, status) => {
  const token = await getToken();
  return axios
    .patch(
      `/sns/v1/connections/requests/${id}`,
      { status },
      {
        headers: { Authorization: token },
      },
    )
    .then((res) => res.data);
};

// eslint-disable-next-line no-unused-vars
const deleteConnectionRequest = async (id) => {
  const token = await getToken();
  return axios
    .delete(`/sns/v1/connections/requests/${id}`, {
      headers: { Authorization: token },
    })
    .then((res) => res.data);
};

export const useConnectionRequests = (
  userId,
  filterType,
  status,
  options = {},
) => {
  const { enabled = true } = options;
  return useQuery(
    ["get-connection-requests", userId, filterType, status],
    () => getConnectionRequests(userId, filterType, status),
    {
      enabled: !!userId && enabled,
      ...NETWORK_QUERY_OPTIONS,
    },
  );
};

const getFollowers = async (
  userId,
  mode,
  { page = 0, size = 100, sort = "id,DESC" } = {},
) => {
  const token = await getToken();
  const normalizedUserId =
    normalizeNumericIdentifier(userId) ?? normalizeUserIdentifier(userId);
  if (normalizedUserId == null) {
    return [];
  }
  const normalizedMode = String(mode || "").toUpperCase();
  const filters = {
    userId: String(normalizedUserId),
    ...(normalizedMode ? { filterType: normalizedMode } : {}),
  };
  const params = {
    ...filters,
    page,
    size,
    sort,
  };
  if (import.meta.env.DEV) {
    console.debug(
      `[SNS followers] request mode=${mode || "all"} userId=${userId} normalizedUserId=${normalizedUserId} params=${JSON.stringify(params)}`,
    );
  }
  return axios
    .get(`/sns/v1/followers`, {
      headers: { Authorization: token },
      params,
    })
    .then((res) => {
      if (import.meta.env.DEV) {
        const content = Array.isArray(res.data?.content)
          ? res.data.content
          : Array.isArray(res.data)
            ? res.data
            : [];
        console.debug(
          `[SNS followers] response mode=${mode || "all"} status=${res.status} contentLength=${content.length} firstItem=${JSON.stringify(content[0] || null)} rawKeys=${Object.keys(res.data || {}).join(",")}`,
        );
      }
      return res.data;
    })
    .catch((error) => {
      if (import.meta.env.DEV) {
        console.debug(
          `[SNS followers] error mode=${mode || "all"} status=${error?.response?.status} params=${JSON.stringify(params)} data=${JSON.stringify(error?.response?.data || null)}`,
        );
      }
      return logSnsError(`followers-${mode || "all"}`, error);
    });
};

export const useFollowers = (userId, mode, options = {}) => {
  const { enabled = true } = options;

  return useQuery(
    ["get-followers", userId, mode],
    () => getFollowers(userId, mode),
    {
      enabled: Boolean(userId) && enabled,
      ...NETWORK_QUERY_OPTIONS,
    },
  );
};

export const useMostViewedKeywords = () => {
  return useInfiniteQuery(
    ["get-most-viewed-keywords"],
    async ({ pageParam = 1 }) => {
      const pageIndex = Math.max(0, Number(pageParam) - 1);
      const response = await getMostViewedKeywords({
        page: pageIndex,
        size: 10,
      });
      return response;
    },
    {
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      getNextPageParam: (lastPage) => {
        const { pagination } = lastPage;
        if (pagination.page < pagination.totalPages) {
          return pagination.page + 1;
        } else {
          return null;
        }
      },
    },
  );
};

export const useMostViewedLists = () => {
  return useInfiniteQuery(
    ["get-most-viewed-lists"],
    async ({ pageParam = 1 }) => {
      const pageIndex = Math.max(0, Number(pageParam) - 1);
      const response = await getMostViewedLists({ page: pageIndex, size: 10 });
      return response;
    },
    {
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      getNextPageParam: (lastPage) => {
        const { pagination } = lastPage;
        if (pagination.page < pagination.totalPages) {
          return pagination.page + 1;
        } else {
          return null;
        }
      },
    },
  );
};

export const useTrendingKeywords = () => {
  return useInfiniteQuery(
    ["get-trending-keywords"],
    async ({ pageParam = 1 }) => {
      const pageIndex = Math.max(0, Number(pageParam) - 1);
      const response = await getTrendingKeywords({ page: pageIndex, size: 10 });
      return response;
    },
    {
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      getNextPageParam: (lastPage) => {
        const { pagination } = lastPage;
        if (pagination.page < pagination.totalPages) {
          return pagination.page + 1;
        } else {
          return null;
        }
      },
    },
  );
};

export const useTrendingLists = () => {
  return useInfiniteQuery(
    ["get-trending-lists"],
    async ({ pageParam = 1 }) => {
      const pageIndex = Math.max(0, Number(pageParam) - 1);
      const response = await getTrendingLists({ page: pageIndex, size: 10 });
      return response;
    },
    {
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      getNextPageParam: (lastPage) => {
        const { pagination } = lastPage;
        if (pagination.page < pagination.totalPages) {
          return pagination.page + 1;
        } else {
          return null;
        }
      },
    },
  );
};

export const useUserHasSubscription = () => {
  return useQuery({
    queryKey: ["user-has-any-active-subscription"],
    queryFn: async () => {
      try {
        return await getActiveSubscription();
      } catch {
        return {
          subscriptions: [],
          active: null,
          rawResponse: null,
        };
      }
    },
    select: (data) => {
      const activeStatus = String(data?.active?.status || "")
        .trim()
        .toUpperCase();
      return ["ACTIVE", "TRIALING", "PAST_DUE"].includes(activeStatus);
    },
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const ENTITLEMENT_FEATURE_KEYS = {
  MAX_LISTS_TOTAL: "MAX_LISTS_TOTAL",
  MAX_KEYWORDS_PER_LIST: "MAX_KEYWORDS_PER_LIST",
  AI_KEYWORD_BULK_GENERATION_CAP: "AI_KEYWORD_BULK_GENERATION_CAP",
  KAIL_USAGE_CAP_BY_PROMPTS: "KAIL_USAGE_CAP_BY_PROMPTS",
  MONTHLY_OWN_GAMES_REPLAYS: "MONTHLY_OWN_GAMES_REPLAYS",
  MONTHLY_CHALLENGED_GAMES_PLAY: "MONTHLY_CHALLENGED_GAMES_PLAY",
  MONTHLY_CHALLENGED_LISTS_PLAY: "MONTHLY_CHALLENGED_LISTS_PLAY",
  KEYWORD_DEFINITION_REGENERATIONS_PER_KEYWORD:
    "KEYWORD_DEFINITION_REGENERATIONS_PER_KEYWORD",
  MONTHLY_AI_LIST_CREATIONS: "MONTHLY_AI_LIST_CREATIONS",
  MONTHLY_AI_KEYWORD_CREATIONS: "MONTHLY_AI_KEYWORD_CREATIONS",
  MONTHLY_AI_DEFINITION_GENERATIONS: "MONTHLY_AI_DEFINITION_GENERATIONS",
  KAIL_CHATBOT: "KAIL_CHATBOT",
  AI_DEFINITION_REGENERATION: "AI_DEFINITION_REGENERATION",
  MONTHLY_GAMES_PLAYED: "MONTHLY_GAMES_PLAYED",
  CORE_QUIZZES: "CORE_QUIZZES",
  EARLY_ACCESS: "EARLY_ACCESS",
  LEARNING_INSIGHTS: "LEARNING_INSIGHTS",
};

const normalizeEntitlementFeatureKey = (value) =>
  String(value || "")
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toUpperCase();

const KAIL_CHATBOT_FEATURE_ALIASES = [
  ENTITLEMENT_FEATURE_KEYS.KAIL_CHATBOT,
  "KAIL",
  "KAIL_ASSISTANT",
  "KAIL_LIST_ASSISTANT",
  "AI_CHATBOT",
  "CHATBOT",
  "AI_ASSISTANT",
  "LIST_ASSISTANT",
].map(normalizeEntitlementFeatureKey);

const AI_DEFINITION_REGENERATION_FEATURE_ALIASES = [
  ENTITLEMENT_FEATURE_KEYS.AI_DEFINITION_REGENERATION,
  "AI_DEFINITION_GENERATION",
  "AI_DEFINITION_GENERATIONS",
  "AI_KEYWORD_DEFINITION_GENERATION",
  "AI_KEYWORD_DEFINITION_REGENERATION",
  "KAIL_DEFINITION_GENERATION",
  "KAIL_DEFINITION_REGENERATION",
].map(normalizeEntitlementFeatureKey);

const MONTHLY_AI_DEFINITION_GENERATION_ALIASES = [
  ENTITLEMENT_FEATURE_KEYS.MONTHLY_AI_DEFINITION_GENERATIONS,
  ENTITLEMENT_FEATURE_KEYS.KEYWORD_DEFINITION_REGENERATIONS_PER_KEYWORD,
  "MONTHLY_KEYWORD_DEFINITIONS_GENERATIONS",
  "MONTHLY_AI_DEFINITION_REGENERATIONS",
  "MONTHLY_AI_KEYWORD_DEFINITION_GENERATIONS",
  "MONTHLY_AI_KEYWORD_DEFINITION_REGENERATIONS",
  "AI_DEFINITION_GENERATIONS_MONTHLY",
  "AI_DEFINITION_REGENERATIONS_MONTHLY",
].map(normalizeEntitlementFeatureKey);

const KAIL_USAGE_CAP_ALIASES = [
  ENTITLEMENT_FEATURE_KEYS.KAIL_USAGE_CAP_BY_PROMPTS,
  "KAIL_PROMPT_CAP",
  "KAIL_PROMPTS_CAP",
  "KAIL_MONTHLY_PROMPT_CAP",
  "MONTHLY_KAIL_USAGE_CAP_BY_PROMPTS",
  "MONTHLY_KAIL_PROMPTS",
].map(normalizeEntitlementFeatureKey);

const AI_KEYWORD_BULK_GENERATION_CAP_ALIASES = [
  ENTITLEMENT_FEATURE_KEYS.AI_KEYWORD_BULK_GENERATION_CAP,
  ENTITLEMENT_FEATURE_KEYS.MONTHLY_AI_LIST_CREATIONS,
  ENTITLEMENT_FEATURE_KEYS.MONTHLY_AI_KEYWORD_CREATIONS,
  "MONTHLY_AI_KEYWORD_BULK_GENERATION_CAP",
  "AI_BULK_KEYWORD_GENERATION_CAP",
  "BULK_KEYWORD_GENERATION_CAP",
  "MONTHLY_BULK_KEYWORD_GENERATIONS",
].map(normalizeEntitlementFeatureKey);

const KEYWORD_DEFINITION_REGENERATION_LIMIT_ALIASES = [
  ENTITLEMENT_FEATURE_KEYS.KEYWORD_DEFINITION_REGENERATIONS_PER_KEYWORD,
  "KEYWORD_DEFINITION_GENERATIONS_PER_KEYWORD",
  "AI_DEFINITION_REGENERATIONS_PER_KEYWORD",
  "AI_KEYWORD_DEFINITION_REGENERATIONS_PER_KEYWORD",
  ...MONTHLY_AI_DEFINITION_GENERATION_ALIASES,
].map(normalizeEntitlementFeatureKey);

const OWN_GAME_REPLAY_LIMIT_ALIASES = [
  ENTITLEMENT_FEATURE_KEYS.MONTHLY_OWN_GAMES_REPLAYS,
  "OWN_GAMES_REPLAYS",
  "OWN_GAME_REPLAYS",
  "OWN_GAME_REPLAY_LIMIT",
].map(normalizeEntitlementFeatureKey);

const CHALLENGED_GAMES_PLAY_LIMIT_ALIASES = [
  ENTITLEMENT_FEATURE_KEYS.MONTHLY_CHALLENGED_GAMES_PLAY,
  "CHALLENGED_GAMES_PLAY",
  "CHALLENGED_GAME_PLAYS",
  "RECEIVED_CHALLENGES_PLAY",
  "RECEIVED_CHALLENGE_PLAYS",
].map(normalizeEntitlementFeatureKey);

const CHALLENGED_LISTS_PLAY_LIMIT_ALIASES = [
  ENTITLEMENT_FEATURE_KEYS.MONTHLY_CHALLENGED_LISTS_PLAY,
  "CHALLENGED_LISTS_PLAY",
  "CHALLENGED_LIST_PLAY_LIMIT",
  "RECEIVED_CHALLENGES_PER_LIST_PLAY",
  "RECEIVED_CHALLENGE_PER_LIST_PLAYS",
].map(normalizeEntitlementFeatureKey);

const normalizeEntitlementCollection = (collection) => {
  if (Array.isArray(collection)) {
    return collection.reduce((acc, entry) => {
      const featureKey = normalizeEntitlementFeatureKey(
        entry?.featureKey ||
          entry?.featureCode ||
          entry?.code ||
          entry?.name ||
          entry?.key,
      );
      if (!featureKey) return acc;
      acc[featureKey] = entry;
      return acc;
    }, {});
  }

  if (collection && typeof collection === "object") {
    return Object.entries(collection).reduce((acc, [key, value]) => {
      const featureKey = normalizeEntitlementFeatureKey(key);
      if (!featureKey) return acc;
      acc[featureKey] = value;
      return acc;
    }, {});
  }

  return {};
};

const normalizeEntitlementValue = (entry) => {
  if (entry == null) return null;

  const typedValue = entry?.value ?? entry?.typedValue ?? entry;
  const declaredType = String(entry?.type || "").toUpperCase();

  if (declaredType === "NUMBER") {
    const numericValue = Number(typedValue);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  if (declaredType === "BOOLEAN") {
    if (typeof typedValue === "boolean") return typedValue;
    if (typeof typedValue === "string") {
      const lowered = typedValue.trim().toLowerCase();
      if (lowered === "true") return true;
      if (lowered === "false") return false;
    }
    return Boolean(typedValue);
  }

  if (declaredType === "ENUM") {
    return typedValue == null ? null : String(typedValue).toUpperCase();
  }

  if (typeof typedValue === "string") {
    const trimmed = typedValue.trim();
    if (!trimmed.length) return "";
    const lowered = trimmed.toLowerCase();
    if (lowered === "true") return true;
    if (lowered === "false") return false;

    const numericValue = Number(trimmed);
    if (Number.isFinite(numericValue) && trimmed !== "") {
      return numericValue;
    }

    return trimmed;
  }

  return typedValue;
};

const normalizeEntitlementStrategy = (entry) => {
  const strategy = String(entry?.strategy || "")
    .trim()
    .toUpperCase();
  return strategy || null;
};

const normalizeEntitlementUsageCount = (entry) => {
  const numericValue = Number(
    entry?.used ??
      entry?.usedCount ??
      entry?.count ??
      entry?.usage ??
      entry?.currentUsage ??
      entry?.consumed ??
      entry?.value ??
      entry,
  );
  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return 0;
  }

  return Math.max(0, Math.trunc(numericValue));
};

const normalizeEntitlementPeriodBoundary = (value) => {
  if (value == null || value === "") return null;

  if (typeof value === "number" || /^\d+$/.test(String(value).trim())) {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue <= 0) return null;
    const timestampMs =
      numericValue < 10_000_000_000 ? numericValue * 1000 : numericValue;
    const parsedDate = new Date(timestampMs);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate.toISOString();
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate.toISOString();
};

const normalizePackageCode = (value) => {
  const normalizedValue = String(value || "FREE")
    .trim()
    .toUpperCase();

  if (normalizedValue === "BASIC") {
    return "FREE";
  }

  if (["FREE", "ENHANCED", "PREMIUM"].includes(normalizedValue)) {
    return normalizedValue;
  }

  return "FREE";
};

export const normalizeUserEntitlements = (payload) => {
  const entitlementMap = normalizeEntitlementCollection(
    payload?.entitlements || payload?.features,
  );
  const usageMap = normalizeEntitlementCollection(
    payload?.usage ||
      payload?.usages ||
      payload?.featureUsage ||
      payload?.featureUsages ||
      payload?.usageCounts,
  );

  const getFeature = (featureKey, fallback = null, aliases = []) => {
    const featureKeys = [featureKey, ...aliases]
      .map(normalizeEntitlementFeatureKey)
      .filter(Boolean);

    for (const nextFeatureKey of featureKeys) {
      const normalizedValue = normalizeEntitlementValue(
        entitlementMap?.[nextFeatureKey],
      );
      if (normalizedValue != null) {
        return normalizedValue;
      }
    }

    return fallback;
  };

  const getUsage = (featureKey, aliases = []) => {
    const featureKeys = [featureKey, ...aliases]
      .map(normalizeEntitlementFeatureKey)
      .filter(Boolean);

    for (const nextFeatureKey of featureKeys) {
      if (Object.prototype.hasOwnProperty.call(usageMap, nextFeatureKey)) {
        return normalizeEntitlementUsageCount(usageMap?.[nextFeatureKey]);
      }
    }

    return 0;
  };

  const getStrategy = (featureKey, aliases = []) => {
    const featureKeys = [featureKey, ...aliases]
      .map(normalizeEntitlementFeatureKey)
      .filter(Boolean);

    for (const nextFeatureKey of featureKeys) {
      const normalizedStrategy = normalizeEntitlementStrategy(
        entitlementMap?.[nextFeatureKey],
      );
      if (normalizedStrategy) {
        return normalizedStrategy;
      }
    }

    return null;
  };

  const packageCode = normalizePackageCode(
    payload?.featurePackageCode ??
      payload?.packageCode ??
      payload?.planCode ??
      payload?.subscriptionPackageCode ??
      payload?.package?.code ??
      payload?.plan?.code,
  );
  const hasKailSubscriptionPackage = ["ENHANCED", "PREMIUM"].includes(
    packageCode,
  );
  const explicitKailChatbot = getFeature(
    ENTITLEMENT_FEATURE_KEYS.KAIL_CHATBOT,
    null,
    KAIL_CHATBOT_FEATURE_ALIASES,
  );
  const kailUsageCapByPrompts = getFeature(
    ENTITLEMENT_FEATURE_KEYS.KAIL_USAGE_CAP_BY_PROMPTS,
    0,
    KAIL_USAGE_CAP_ALIASES,
  );
  const aiKeywordBulkGenerationCap = getFeature(
    ENTITLEMENT_FEATURE_KEYS.AI_KEYWORD_BULK_GENERATION_CAP,
    0,
    AI_KEYWORD_BULK_GENERATION_CAP_ALIASES,
  );
  const keywordDefinitionRegenerationsPerKeyword = getFeature(
    ENTITLEMENT_FEATURE_KEYS.KEYWORD_DEFINITION_REGENERATIONS_PER_KEYWORD,
    0,
    KEYWORD_DEFINITION_REGENERATION_LIMIT_ALIASES,
  );
  const monthlyOwnGamesReplays = getFeature(
    ENTITLEMENT_FEATURE_KEYS.MONTHLY_OWN_GAMES_REPLAYS,
    0,
    OWN_GAME_REPLAY_LIMIT_ALIASES,
  );
  const monthlyChallengedGamesPlay = getFeature(
    ENTITLEMENT_FEATURE_KEYS.MONTHLY_CHALLENGED_GAMES_PLAY,
    0,
    CHALLENGED_GAMES_PLAY_LIMIT_ALIASES,
  );
  const monthlyChallengedListsPlay = getFeature(
    ENTITLEMENT_FEATURE_KEYS.MONTHLY_CHALLENGED_LISTS_PLAY,
    0,
    CHALLENGED_LISTS_PLAY_LIMIT_ALIASES,
  );
  const hasKailPromptAllowance = Number(kailUsageCapByPrompts) > 0;

  return {
    raw: payload || null,
    packageCode,
    source: payload?.source || null,
    versionCode: payload?.featurePackageVersionCode ?? null,
    period: {
      start: normalizeEntitlementPeriodBoundary(payload?.period?.start),
      end: normalizeEntitlementPeriodBoundary(payload?.period?.end),
    },
    limits: {
      maxListsTotal: getFeature(ENTITLEMENT_FEATURE_KEYS.MAX_LISTS_TOTAL, 0),
      maxKeywordsPerList: getFeature(
        ENTITLEMENT_FEATURE_KEYS.MAX_KEYWORDS_PER_LIST,
        0,
      ),
      kailUsageCapByPrompts,
      aiKeywordBulkGenerationCap,
      keywordDefinitionRegenerationsPerKeyword,
      monthlyOwnGamesReplays,
      monthlyChallengedGamesPlay,
      monthlyChallengedListsPlay,
      monthlyAiListCreations: aiKeywordBulkGenerationCap,
      monthlyAiKeywordCreations: aiKeywordBulkGenerationCap,
      monthlyAiDefinitionGenerations: keywordDefinitionRegenerationsPerKeyword,
      monthlyGamesPlayed: 0,
    },
    usage: {
      kailUsageCapByPrompts: getUsage(
        ENTITLEMENT_FEATURE_KEYS.KAIL_USAGE_CAP_BY_PROMPTS,
        KAIL_USAGE_CAP_ALIASES,
      ),
      aiKeywordBulkGenerationCap: getUsage(
        ENTITLEMENT_FEATURE_KEYS.AI_KEYWORD_BULK_GENERATION_CAP,
        AI_KEYWORD_BULK_GENERATION_CAP_ALIASES,
      ),
      keywordDefinitionRegenerationsPerKeyword: getUsage(
        ENTITLEMENT_FEATURE_KEYS.KEYWORD_DEFINITION_REGENERATIONS_PER_KEYWORD,
        KEYWORD_DEFINITION_REGENERATION_LIMIT_ALIASES,
      ),
      monthlyOwnGamesReplays: getUsage(
        ENTITLEMENT_FEATURE_KEYS.MONTHLY_OWN_GAMES_REPLAYS,
        OWN_GAME_REPLAY_LIMIT_ALIASES,
      ),
      monthlyChallengedGamesPlay: getUsage(
        ENTITLEMENT_FEATURE_KEYS.MONTHLY_CHALLENGED_GAMES_PLAY,
        CHALLENGED_GAMES_PLAY_LIMIT_ALIASES,
      ),
      monthlyChallengedListsPlay: getUsage(
        ENTITLEMENT_FEATURE_KEYS.MONTHLY_CHALLENGED_LISTS_PLAY,
        CHALLENGED_LISTS_PLAY_LIMIT_ALIASES,
      ),
      monthlyAiListCreations: getUsage(
        ENTITLEMENT_FEATURE_KEYS.AI_KEYWORD_BULK_GENERATION_CAP,
        AI_KEYWORD_BULK_GENERATION_CAP_ALIASES,
      ),
      monthlyAiKeywordCreations: getUsage(
        ENTITLEMENT_FEATURE_KEYS.AI_KEYWORD_BULK_GENERATION_CAP,
        AI_KEYWORD_BULK_GENERATION_CAP_ALIASES,
      ),
      monthlyAiDefinitionGenerations: getUsage(
        ENTITLEMENT_FEATURE_KEYS.KEYWORD_DEFINITION_REGENERATIONS_PER_KEYWORD,
        KEYWORD_DEFINITION_REGENERATION_LIMIT_ALIASES,
      ),
      monthlyGamesPlayed: 0,
    },
    strategies: {
      kailUsageCapByPrompts: getStrategy(
        ENTITLEMENT_FEATURE_KEYS.KAIL_USAGE_CAP_BY_PROMPTS,
        KAIL_USAGE_CAP_ALIASES,
      ),
      aiKeywordBulkGenerationCap: getStrategy(
        ENTITLEMENT_FEATURE_KEYS.AI_KEYWORD_BULK_GENERATION_CAP,
        AI_KEYWORD_BULK_GENERATION_CAP_ALIASES,
      ),
      keywordDefinitionRegenerationsPerKeyword: getStrategy(
        ENTITLEMENT_FEATURE_KEYS.KEYWORD_DEFINITION_REGENERATIONS_PER_KEYWORD,
        KEYWORD_DEFINITION_REGENERATION_LIMIT_ALIASES,
      ),
      monthlyOwnGamesReplays: getStrategy(
        ENTITLEMENT_FEATURE_KEYS.MONTHLY_OWN_GAMES_REPLAYS,
        OWN_GAME_REPLAY_LIMIT_ALIASES,
      ),
      monthlyChallengedGamesPlay: getStrategy(
        ENTITLEMENT_FEATURE_KEYS.MONTHLY_CHALLENGED_GAMES_PLAY,
        CHALLENGED_GAMES_PLAY_LIMIT_ALIASES,
      ),
      monthlyChallengedListsPlay: getStrategy(
        ENTITLEMENT_FEATURE_KEYS.MONTHLY_CHALLENGED_LISTS_PLAY,
        CHALLENGED_LISTS_PLAY_LIMIT_ALIASES,
      ),
      monthlyAiListCreations: getStrategy(
        ENTITLEMENT_FEATURE_KEYS.AI_KEYWORD_BULK_GENERATION_CAP,
        AI_KEYWORD_BULK_GENERATION_CAP_ALIASES,
      ),
      monthlyAiKeywordCreations: getStrategy(
        ENTITLEMENT_FEATURE_KEYS.AI_KEYWORD_BULK_GENERATION_CAP,
        AI_KEYWORD_BULK_GENERATION_CAP_ALIASES,
      ),
      monthlyAiDefinitionGenerations: getStrategy(
        ENTITLEMENT_FEATURE_KEYS.KEYWORD_DEFINITION_REGENERATIONS_PER_KEYWORD,
        KEYWORD_DEFINITION_REGENERATION_LIMIT_ALIASES,
      ),
      monthlyGamesPlayed: null,
    },
    features: {
      coreQuizzes: getFeature(ENTITLEMENT_FEATURE_KEYS.CORE_QUIZZES, false),
      kailChatbot:
        hasKailSubscriptionPackage ||
        Boolean(explicitKailChatbot) ||
        hasKailPromptAllowance,
      aiDefinitionRegeneration:
        hasKailSubscriptionPackage ||
        Boolean(
          getFeature(
            ENTITLEMENT_FEATURE_KEYS.AI_DEFINITION_REGENERATION,
            false,
            AI_DEFINITION_REGENERATION_FEATURE_ALIASES,
          ),
        ),
      earlyAccess: getFeature(ENTITLEMENT_FEATURE_KEYS.EARLY_ACCESS, false),
      learningInsights: getFeature(
        ENTITLEMENT_FEATURE_KEYS.LEARNING_INSIGHTS,
        "BASIC",
      ),
    },
  };
};

export const useKailChatbotEntitlementStatus = () => {
  const {
    profile,
    user,
    entitlements: cachedEntitlements,
  } = useSelector((state) => state?.user || {});
  const resolvedUserId = normalizeUserIdentifier(
    resolveUserIdentifier(profile, user),
  );

  const { data: queriedEntitlements, isLoading } = useUserEntitlements(
    resolvedUserId,
    {
      enabled: resolvedUserId != null,
    },
  );

  const entitlements = queriedEntitlements || cachedEntitlements;
  const packageCode = normalizePackageCode(entitlements?.packageCode);
  const hasKailChatbotFeature = Boolean(entitlements?.features?.kailChatbot);
  const kailPromptLimit = Number(
    entitlements?.limits?.kailUsageCapByPrompts ?? 0,
  );
  const kailPromptsUsed = Number(
    entitlements?.usage?.kailUsageCapByPrompts ?? 0,
  );
  const normalizedKailPromptLimit =
    Number.isFinite(kailPromptLimit) && kailPromptLimit > 0
      ? kailPromptLimit
      : 0;
  const normalizedKailPromptsUsed =
    Number.isFinite(kailPromptsUsed) && kailPromptsUsed > 0
      ? Math.min(kailPromptsUsed, normalizedKailPromptLimit)
      : 0;
  const remainingKailPrompts =
    normalizedKailPromptLimit > 0
      ? Math.max(normalizedKailPromptLimit - normalizedKailPromptsUsed, 0)
      : 0;
  const hasKailPromptAllowance =
    normalizedKailPromptLimit > 0 && remainingKailPrompts > 0;

  return {
    packageCode,
    source: entitlements?.source || null,
    period: entitlements?.period || { start: null, end: null },
    canUseKailChatbot: hasKailChatbotFeature && hasKailPromptAllowance,
    hasKailChatbotFeature,
    kailPromptLimit: normalizedKailPromptLimit,
    kailPromptsUsed: normalizedKailPromptsUsed,
    remainingKailPrompts,
    kailPromptTrackingStrategy:
      entitlements?.strategies?.kailUsageCapByPrompts || null,
    isLoading: isLoading && !entitlements,
  };
};

const getUserEntitlements = async (userId) => {
  const normalizedId = normalizeUserIdentifier(userId);
  if (normalizedId == null) {
    throw new Error("userId is required to fetch entitlements");
  }

  const token = await getToken();
  const response = await axios.get(`/pss/entitlements/${normalizedId}`, {
    headers: {
      Authorization: token,
    },
  });

  return normalizeUserEntitlements(response?.data || {});
};

export const useUserEntitlements = (userId, options = {}) => {
  const normalizedId = normalizeUserIdentifier(userId);
  const { enabled = true, staleTime = 5 * 60 * 1000 } = options;

  return useQuery(
    ["user-entitlements", normalizedId],
    () => getUserEntitlements(normalizedId),
    {
      enabled: enabled && normalizedId != null,
      cacheTime: 10 * 60 * 1000,
      staleTime,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  );
};
